import { useState, useEffect, useCallback } from 'react';
import { profileApi } from '../services/profile-api';
import type {
  ChangePasswordData,
  ChangeEmailData,
  MfaToggleData,
  NotificationPreference,
  UpdateNotificationPreferences,
  DataExportRequest,
  AccountDeletionRequest,
} from '../types';

interface UseSettingsReturn {
  // Notification preferences
  notificationPreferences: NotificationPreference[];
  loadingPreferences: boolean;
  fetchNotificationPreferences: () => Promise<void>;
  updateNotificationPreference: (data: UpdateNotificationPreferences) => Promise<void>;

  // Account
  changePassword: (data: ChangePasswordData) => Promise<void>;
  changeEmail: (data: ChangeEmailData) => Promise<void>;
  enableMfa: (data: MfaToggleData) => Promise<{ secret: string; qrCodeUrl: string }>;
  disableMfa: (data: MfaToggleData) => Promise<void>;

  // Data & Privacy
  requestDataExport: () => Promise<DataExportRequest>;
  requestAccountDeletion: (password: string) => Promise<AccountDeletionRequest>;
  cancelAccountDeletion: () => Promise<void>;

  // State
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

export function useSettings(accessToken: string | null): UseSettingsReturn {
  const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreference[]>([]);
  const [loadingPreferences, setLoadingPreferences] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const fetchNotificationPreferences = useCallback(async () => {
    if (!accessToken) return;
    setLoadingPreferences(true);
    try {
      const prefs = await profileApi.getNotificationPreferences(accessToken);
      setNotificationPreferences(prefs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load preferences');
    } finally {
      setLoadingPreferences(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchNotificationPreferences();
  }, [fetchNotificationPreferences]);

  const updateNotificationPreference = useCallback(
    async (data: UpdateNotificationPreferences) => {
      if (!accessToken) throw new Error('Not authenticated');
      setIsLoading(true);
      setError(null);
      try {
        const updated = await profileApi.updateNotificationPreferences(accessToken, data);
        setNotificationPreferences((prev) =>
          prev.map((p) => (p.eventType === updated.eventType ? { ...p, ...updated } : p))
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update preference');
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [accessToken]
  );

  const changePassword = useCallback(
    async (data: ChangePasswordData) => {
      if (!accessToken) throw new Error('Not authenticated');
      setIsLoading(true);
      setError(null);
      try {
        await profileApi.changePassword(accessToken, data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to change password');
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [accessToken]
  );

  const changeEmail = useCallback(
    async (data: ChangeEmailData) => {
      if (!accessToken) throw new Error('Not authenticated');
      setIsLoading(true);
      setError(null);
      try {
        await profileApi.changeEmail(accessToken, data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to change email');
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [accessToken]
  );

  const enableMfa = useCallback(
    async (data: MfaToggleData) => {
      if (!accessToken) throw new Error('Not authenticated');
      setIsLoading(true);
      setError(null);
      try {
        const result = await profileApi.enableMfa(accessToken, data);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to enable MFA');
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [accessToken]
  );

  const disableMfa = useCallback(
    async (data: MfaToggleData) => {
      if (!accessToken) throw new Error('Not authenticated');
      setIsLoading(true);
      setError(null);
      try {
        await profileApi.disableMfa(accessToken, data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to disable MFA');
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [accessToken]
  );

  const requestDataExport = useCallback(async () => {
    if (!accessToken) throw new Error('Not authenticated');
    setIsLoading(true);
    setError(null);
    try {
      const result = await profileApi.requestDataExport(accessToken);
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request data export');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  const requestAccountDeletion = useCallback(
    async (password: string) => {
      if (!accessToken) throw new Error('Not authenticated');
      setIsLoading(true);
      setError(null);
      try {
        const result = await profileApi.requestAccountDeletion(accessToken, password);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to request account deletion');
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [accessToken]
  );

  const cancelAccountDeletion = useCallback(async () => {
    if (!accessToken) throw new Error('Not authenticated');
    setIsLoading(true);
    setError(null);
    try {
      await profileApi.cancelAccountDeletion(accessToken);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel deletion');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  return {
    notificationPreferences,
    loadingPreferences,
    fetchNotificationPreferences,
    updateNotificationPreference,
    changePassword,
    changeEmail,
    enableMfa,
    disableMfa,
    requestDataExport,
    requestAccountDeletion,
    cancelAccountDeletion,
    isLoading,
    error,
    clearError,
  };
}
